import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Building2, Copy, Check, ArrowRight, AlertTriangle } from "lucide-react";
import toast from "react-hot-toast";
import { authApi } from "@/api/endpoints";

interface InviteInfo {
  email: string;
  display_name?: string | null;
  organisation_name?: string | null;
  role_name?: string | null;
  temp_password?: string | null;
  already_used?: boolean;
}

/**
 * /welcome?token=<jwt>
 *
 * Landing page reached from the invite email. Fetches invite details from the
 * backend (validating the signed token) and shows copy-to-clipboard buttons
 * for the email + temp password. "Sign in" passes ?email= to LoginPage so MSAL
 * pre-fills the CIAM email field.
 */
export default function WelcomePage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [info, setInfo] = useState<InviteInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError("This welcome link is missing its invite token.");
      return;
    }
    authApi.inviteInfo(token)
      .then(setInfo)
      .catch((e) => {
        const msg = e?.response?.data?.detail ?? "We couldn't load your invite. The link may have expired.";
        setError(msg);
      });
  }, [token]);

  if (error) {
    return (
      <CenterCard>
        <div className="flex items-center gap-2 text-amber-700 mb-3">
          <AlertTriangle size={20} />
          <h1 className="text-lg font-semibold">Invite link issue</h1>
        </div>
        <p className="text-sm text-slate-600 mb-4">{error}</p>
        <Link to="/" className="btn-primary inline-flex">Go to sign in <ArrowRight size={16} /></Link>
      </CenterCard>
    );
  }

  if (!info) {
    return (
      <CenterCard>
        <p className="text-sm text-slate-500">Loading your invite…</p>
      </CenterCard>
    );
  }

  if (info.already_used) {
    return (
      <CenterCard>
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Welcome back</h1>
        <p className="text-sm text-slate-600 mb-6">
          You've already used this invite. If you've forgotten your password,
          use the "Forgot password" link on the sign-in page.
        </p>
        <Link to={`/?email=${encodeURIComponent(info.email)}`} className="btn-primary">
          Sign in <ArrowRight size={16} />
        </Link>
      </CenterCard>
    );
  }

  return (
    <CenterCard>
      <div className="flex items-center gap-3 mb-5">
        <div className="h-10 w-10 rounded-lg bg-brand-700 flex items-center justify-center">
          <Building2 className="text-white" size={22} />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-slate-900">UmdoniWay</h1>
          <p className="text-xs text-slate-500">Utility monitoring for SA properties</p>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-1">
        Welcome{info.display_name ? `, ${info.display_name.split(" ")[0]}` : ""}!
      </h2>
      <p className="text-sm text-slate-600 mb-5">
        You've been invited to <strong>{info.organisation_name ?? "your organisation"}</strong>
        {info.role_name && <> as <strong>{info.role_name}</strong></>}.
      </p>

      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
        Sign-in details
      </p>
      <CopyRow label="Email" value={info.email} />
      {info.temp_password && (
        <CopyRow label="Temporary password" value={info.temp_password} highlight />
      )}

      <p className="text-xs text-slate-500 mt-3 mb-6">
        You'll be asked to set your own password the first time you sign in.
      </p>

      <Link
        to={`/?email=${encodeURIComponent(info.email)}`}
        className="btn-primary w-full py-3 justify-center"
      >
        Sign in now <ArrowRight size={16} />
      </Link>
    </CenterCard>
  );
}

function CenterCard({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 via-white to-slate-100 px-4">
      <div className="w-full max-w-md card p-8">{children}</div>
    </div>
  );
}

function CopyRow({ label, value, highlight = false }: { label: string; value: string; highlight?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't access clipboard");
    }
  };
  return (
    <div className="flex items-stretch mb-2">
      <div className="flex-1 border border-slate-200 rounded-l-md bg-slate-50 px-3 py-2.5 overflow-x-auto">
        <div className="text-[10px] uppercase tracking-wide text-slate-500 font-medium">{label}</div>
        <div className={`font-mono text-sm ${highlight ? "font-semibold text-amber-900" : "text-slate-800"} truncate`}>
          {value}
        </div>
      </div>
      <button
        type="button"
        onClick={copy}
        className={`px-4 border border-l-0 border-slate-200 rounded-r-md text-xs font-medium ${
          copied ? "bg-brand-50 text-brand-700" : "bg-white hover:bg-slate-50 text-slate-700"
        }`}
        title={`Copy ${label.toLowerCase()}`}
      >
        {copied ? <Check size={16} /> : <Copy size={16} />}
      </button>
    </div>
  );
}
